/**
 * The control that puts a partner unit on your Favorites page, and takes it off again.
 *
 * A button rather than a checkbox, because it acts on the press rather than collecting a
 * value to be submitted later — there is no Save anywhere near it, and a tick-box with no
 * form around it invites people to look for one.
 *
 * WHOSE FAVOURITE IT IS, IS NEVER SENT. The request names a partner unit and a method and
 * nothing else; the Worker reads the owner off the authenticated principal. So this
 * component cannot arrange somebody else's page even if it were asked to, and there is no
 * account id anywhere in the client for anybody to try it with.
 *
 * `on` comes from the row it was rendered with — `is_favorite`, which travels on the same
 * query as the unit itself so the two can never disagree. What comes back from a press is
 * the state the database settled on rather than the state we asked for, which is what
 * makes an impatient double-click end up somewhere true instead of somewhere assumed.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useToast } from './ui'
import { relabelText, usePartnerTerm } from '../lib/terms'
import { Icon } from './Icon'

export default function FavoriteButton({ partnerId, name, on, onChange, size = 'sm' }) {
  const term = usePartnerTerm()
  const qc = useQueryClient()
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const toggle = async (e) => {
    // Both, and for two different reasons: these sit inside a card that navigates on
    // click, and inside a table row that does the same.
    e.preventDefault()
    e.stopPropagation()
    if (busy) return
    setBusy(true)
    try {
      const res = await api(`favorites/${partnerId}`, { method: on ? 'DELETE' : 'PUT' })
      onChange?.(partnerId, !!res.is_favorite)
      // The directory, so a star pressed on one page is right on the next; and the
      // sidebar's own count, which is a query of its own in the shell.
      qc.invalidateQueries({ queryKey: ['partners'] })
      qc.invalidateQueries({ queryKey: ['favorites'] })
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const label = relabelText(
    on
      ? `Remove ${name || 'this partner unit'} from your Favorites`
      : `Add ${name || 'this partner unit'} to your Favorites`,
    term
  )

  return (
    <button
      type="button"
      className={`btn-fav${on ? ' is-on' : ''}${size ? ` btn-fav-${size}` : ''}`}
      onClick={toggle}
      disabled={busy}
      title={label}
      aria-label={label}
      /* The heart carries the state, so it has to be announced rather than only drawn —
         "pressed" is what a screen reader reads out of a toggle. */
      aria-pressed={!!on}
    >
      <Icon name={on ? 'heart' : 'heart-outline'} size={18} />
    </button>
  )
}
